if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend, changeSlide) {
        if (!this.elements || !this.elements.viewer) {
          this.elements = {
            liveRegion: this.querySelector('[id^="GalleryStatus"]'),
            viewer: this.querySelector('[id^="GalleryViewer"]'),
            thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
          };
        }

        const isSlider = this.elements.viewer?.classList.contains('swiper-initialized');
        if (isSlider) {
          const activeSlider = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`);
          if (activeSlider && this.elements.viewer.swiper) {
            const slideIndex = Array.from(this.elements.viewer.swiper.slides).indexOf(activeSlider);
            if (slideIndex !== -1) this.elements.viewer.swiper.slideTo(slideIndex);
          }

          if (!this.elements.thumbnails) return;
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
          this.setActiveThumbnail(activeThumbnail);
          return;
        }

        const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`);
        if (!activeMedia) return;

        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia.classList.add('is-active');

        if (prepend) {
          activeMedia.parentElement.prepend(activeMedia);
          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            if (activeThumbnail) activeThumbnail.parentElement.prepend(activeThumbnail);
          }
          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          const activeMediaRect = activeMedia.getBoundingClientRect();
          if (activeMediaRect.top > -0.5) return;
          window.scrollTo({ top: activeMediaRect.top + window.scrollY, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        if (activeThumbnail) this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button')?.setAttribute('aria-current', true);

        if (
          typeof this.elements.thumbnails.isSlideVisible === 'function' &&
          this.elements.thumbnails.isSlideVisible(thumbnail, 10)
        ) {
          return;
        }

        if (this.elements.thumbnails.slider) {
          this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
        }
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image || !this.elements.liveRegion) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          window.setTimeout(() => this.elements.liveRegion.setAttribute('aria-hidden', true), 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        const controller = activeItem.querySelector("ft-gallery-video-autoplay");
        if (controller) { controller.schedule(); return; }
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}

if (!customElements.get('media-gallery-slider')) {
  class MediaGallerySlider extends HTMLElement {
    connectedCallback() {
      if (this.galleryInitialized) return;
      this.galleryInitialized = true;

      this.swiper = null;
      this.thumbnails = this.closest('media-gallery')?.querySelector('.thumbnail-slider');
      this.mediaGallery = this.closest('media-gallery');
      this.pagination = this.querySelector('.swiper-pagination');
      this.previousWidth = 0;
      this.mobileTrack = null;
      this.mobileCounter = null;
      this.mobileScrollHandler = null;

      this.swiperInitialization = this.swiperInitialization.bind(this);
      this.handleResize = debounce(this.swiperInitialization, 200);

      this.swiperInitialization();
      window.addEventListener('resize', this.handleResize, { passive: true });
      window.addEventListener('orientationchange', this.handleResize, { passive: true });
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.handleResize);
      window.removeEventListener('orientationchange', this.handleResize);
      if (this.swiper) {
        this.swiper.destroy(true, true);
        this.swiper = null;
      }
      this.disableMobileGallery();
      this.galleryInitialized = false;
    }

    initSwiper() {
      const nextBtn = this.querySelector('.swiper-button-next');
      const prevBtn = this.querySelector('.swiper-button-prev');
      const paginationElement = this.querySelector('.swiper-pagination');

      this.swiper = new Swiper(this, {
        effect: 'fade',
        grabCursor: true,
        slidesPerView: 1,
        spaceBetween: 0,
        watchOverflow: true,
        observer: true,
        observeParents: true,
        navigation:
          nextBtn && prevBtn
            ? {
                nextEl: nextBtn,
                prevEl: prevBtn,
              }
            : false,
        pagination: paginationElement
          ? {
              el: paginationElement,
              clickable: true,
            }
          : false,
        on: {
          slideChange: () => {
            this.pauseVideos();
            this.setActiveThumbnail();
            this.updateBullets();
          },
          transitionEnd: () => this.updateBullets(),
        },
      });
    }

    enableMobileGallery() {
      const track = this.querySelector('.swiper-wrapper');
      if (!track) return;

      this.classList.add('patpat-mobile-gallery');
      this.mobileTrack = track;

      if (!this.mobileCounter) {
        this.mobileCounter = document.createElement('div');
        this.mobileCounter.className = 'patpat-gallery-counter';
        this.mobileCounter.setAttribute('aria-live', 'polite');
        track.insertAdjacentElement('afterend', this.mobileCounter);
      }

      this.mobileScrollHandler = debounce(() => this.updateMobileGallery(), 80);
      track.addEventListener('scroll', this.mobileScrollHandler, { passive: true });
      window.requestAnimationFrame(() => this.updateMobileGallery());
    }

    disableMobileGallery() {
      if (this.mobileTrack && this.mobileScrollHandler) {
        this.mobileTrack.removeEventListener('scroll', this.mobileScrollHandler);
      }
      this.classList.remove('patpat-mobile-gallery');
      this.mobileCounter?.remove();
      this.mobileTrack = null;
      this.mobileCounter = null;
      this.mobileScrollHandler = null;
    }

    updateMobileGallery() {
      if (!this.mobileTrack || !this.mobileCounter) return;

      const slides = Array.from(this.mobileTrack.querySelectorAll('.swiper-slide'));
      if (!slides.length) return;

      const targetLeft = this.mobileTrack.scrollLeft + 16;
      let activeIndex = 0;
      let nearestDistance = Infinity;

      slides.forEach((slide, index) => {
        const distance = Math.abs(slide.offsetLeft - targetLeft);
        if (distance < nearestDistance) {
          nearestDistance = distance;
          activeIndex = index;
        }
      });

      slides.forEach((slide, index) => {
        slide.classList.toggle('is-active', index === activeIndex);
      });
      this.mobileCounter.textContent = `${activeIndex + 1}/${slides.length}`;
    }

    swiperInitialization() {
      const currentWidth = Math.min(window.innerWidth, document.documentElement.clientWidth);
      if (this.previousWidth === currentWidth) return;

      const isMobile = currentWidth < 750;
      const initSliderPc = this.dataset.initSliderPc;

      if (this.swiper) {
        this.swiper.destroy(true, true);
        this.swiper = null;
      }

      this.disableMobileGallery();

      this.previousWidth = currentWidth;

      /* PatPat 风格：手机端使用原生横向滚动，不初始化 Swiper fade。 */
      if (isMobile) {
        this.enableMobileGallery();
        return;
      }

      if (typeof window.Swiper !== 'function') {
        this.previousWidth = 0;
        window.setTimeout(this.swiperInitialization, 100);
        return;
      }

      /* 桌面端保持主题原来的 Swiper 行为。 */
      if (initSliderPc === 'true') this.initSwiper();
    }

    pauseVideos() {
      if (!this.swiper) return;

      this.swiper.slides.forEach((slide) => {
        slide.querySelectorAll('video, iframe').forEach((video) => {
          if (video.tagName === 'VIDEO') {
            if (!video.paused) video.pause();
            return;
          }

          const youtubeSrc = video.src || '';
          if (youtubeSrc.includes('youtube.com') || youtubeSrc.includes('youtu.be')) {
            video.contentWindow?.postMessage(
              '{"event":"command","func":"pauseVideo","args":""}',
              '*'
            );
          }
        });
      });
    }

    setActiveThumbnail() {
      if (!this.thumbnails || !this.mediaGallery?.elements?.thumbnails || !this.swiper) return;

      const activeSlide = this.swiper.slides[this.swiper.activeIndex];
      const targetId = activeSlide?.getAttribute('data-media-id');
      if (!targetId) return;

      const thumbnail = this.mediaGallery.elements.thumbnails.querySelector(
        `[data-target="${targetId}"]`
      );
      if (!thumbnail) return;

      if (typeof this.mediaGallery.setActiveThumbnail === 'function') {
        this.mediaGallery.setActiveThumbnail(thumbnail);
      }
    }

    updateBullets() {
      if (!this.swiper?.pagination || !this.pagination) return;
      this.swiper.pagination.update();
    }
  }

  customElements.define('media-gallery-slider', MediaGallerySlider);
}

/* Hosted product videos: autoplay only the selected, visible slide. */
if (!customElements.get('ft-gallery-video-autoplay')) {
  customElements.define('ft-gallery-video-autoplay', class extends HTMLElement {
    connectedCallback() {
      this.frame = 0;
      this.eligible = false;
      this.visible = false;
      this.box = this.closest('.product-media-container');
      this.slide = this.closest('.product__media-item, .swiper-slide');
      this.viewer = this.closest('media-gallery-slider') || this.closest('media-gallery');
      this.deferred = this.box?.querySelector('deferred-media');
      if (!this.slide || !this.viewer || !this.deferred) return;
      this.schedule = this.schedule.bind(this);
      this.intersection = new IntersectionObserver(entries => {
        this.visible = entries[0].isIntersecting && entries[0].intersectionRatio >= 0.5;
        this.schedule();
      }, { threshold: [0, 0.5, 1] });
      this.intersection.observe(this.box);
      this.mutations = new MutationObserver(this.schedule);
      this.mutations.observe(this.viewer, {subtree:true, attributes:true, attributeFilter:['class']});
      this.viewer.addEventListener('scroll', this.schedule, true);
      document.addEventListener('visibilitychange', this.schedule);
      this.schedule();
    }
    schedule() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => { this.frame = 0; this.update(); });
    }
    selected() {
      const swiper = this.viewer.swiper;
      if (swiper && !swiper.destroyed && this.viewer.classList.contains('swiper-initialized')) {
        return swiper.slides[swiper.activeIndex] === this.slide;
      }
      const slides = [...this.slide.parentElement.children].filter(el => el.matches('.product__media-item, .swiper-slide'));
      if (window.innerWidth < 750 && this.viewer.classList.contains('patpat-mobile-gallery')) {
        const track = this.slide.parentElement;
        const center = track.getBoundingClientRect().left + track.clientWidth / 2;
        const nearest = slides.reduce((best, slide) => {
          const r = slide.getBoundingClientRect();
          const distance = Math.abs(r.left + r.width / 2 - center);
          return !best || distance < best.distance ? {slide, distance} : best;
        }, null);
        return nearest?.slide === this.slide;
      }
      return (slides.find(el => el.classList.contains('is-active')) || slides[0]) === this.slide;
    }
    loadVideo() {
      if (this.video) return this.video;
      let video = this.deferred.querySelector('video');
      if (!video) {
        video = this.deferred.querySelector('template')?.content.querySelector('video')?.cloneNode(true);
        if (!video) return null;
        video.removeAttribute('autoplay');
        video.muted = true;
        video.defaultMuted = true;
        video.playsInline = true;
        video.controls = true;
        this.deferred.append(video);
      }
      this.video = video;
      this.installCover(video);
      video.muted = true;
      video.playsInline = true;
      video.controls = true;
      video.removeAttribute('autoplay');
      this.deferred.setAttribute('loaded', '');
      this.deferred.querySelector('.deferred-media__poster')?.style.setProperty('display','none','important');
      // Keep the existing poster's layout as the size placeholder, with the video over it.
      const opener = this.box.querySelector('modal-opener');
      if (opener) {
        opener.style.setProperty('display','block','important');
        opener.style.setProperty('visibility','hidden','important');
        opener.setAttribute('aria-hidden','true');
      }
      this.box.style.setProperty('position','relative');
      for (const [key,value] of Object.entries({display:'block',position:'absolute',inset:'0',width:'100%',height:'100%',padding:'0', 'min-height':'0'})) {
        this.deferred.style.setProperty(key,value,'important');
      }
      for (const [key,value] of Object.entries({width:'100%',height:'100%',position:'absolute',inset:'0','object-fit':'cover'})) {
        video.style.setProperty(key,value,'important');
      }
      return video;
    }
    installCover(video) {
      const source = this.box.querySelector('modal-opener img');
      if (!source) return;
      const cover = document.createElement('button');
      cover.type = 'button';
      cover.setAttribute('aria-label', 'Play video');
      cover.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;z-index:4;border:0;padding:0;background:transparent;cursor:pointer;border-radius:inherit;overflow:hidden;';
      const image = source.cloneNode(true);
      image.loading = 'eager';
      image.removeAttribute('id');
      image.alt = '';
      image.style.cssText = 'display:block;position:absolute;inset:0;width:100%;height:100%;object-fit:cover;';
      cover.append(image);
      const icon = document.createElement('span');
      icon.textContent = '▶';
      icon.setAttribute('aria-hidden', 'true');
      icon.style.cssText = 'position:absolute;left:16px;top:16px;display:grid;place-items:center;width:40px;height:40px;border-radius:50%;background:rgba(0,0,0,.65);color:white;font-size:18px;';
      cover.append(icon);
      this.box.append(cover);
      this.cover = cover;
      // Opacity keeps the video renderable while the cover protects against blank frames.
      video.style.setProperty('opacity', '0');
      if (!video.poster) video.poster = source.currentSrc || source.src;
      this.revealVideo = () => {
        if (!this.isConnected || video.readyState < 2) return;
        video.style.setProperty('opacity', '1');
        cover.style.display = 'none';
      };
      this.onVideoPlaying = () => {
        if (typeof video.requestVideoFrameCallback === 'function') {
          if (this.videoFrame) video.cancelVideoFrameCallback(this.videoFrame);
          this.videoFrame = video.requestVideoFrameCallback(() => {
            this.videoFrame = 0;
            this.revealVideo();
          });
        } else {
          this.revealVideo();
        }
      };
      this.onVideoError = () => {
        video.style.setProperty('opacity', '0');
        cover.style.display = 'block';
      };
      video.addEventListener('playing', this.onVideoPlaying);
      video.addEventListener('error', this.onVideoError);
      cover.addEventListener('click', () => {
        if (video.error) video.load();
        video.play()?.catch(() => {});
      });
      if (!video.paused && video.readyState >= 2) this.onVideoPlaying();
    }
    update() {
      if (!this.isConnected) return;
      const eligible = this.visible && !document.hidden && this.selected();
      if (eligible === this.eligible) return;
      this.eligible = eligible;
      if (!eligible) { this.video?.pause(); return; }
      const video = this.loadVideo();
      if (!video || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      video.muted = true;
      const result = video.play();
      // Native controls remain usable if the browser blocks autoplay.
      result?.then(() => { if (!this.eligible || !this.isConnected) video.pause(); }).catch(() => {});
    }
    disconnectedCallback() {
      cancelAnimationFrame(this.frame);
      this.intersection?.disconnect();
      this.mutations?.disconnect();
      this.viewer?.removeEventListener('scroll',this.schedule,true);
      document.removeEventListener('visibilitychange',this.schedule);
      this.video?.pause();
      if (this.videoFrame) this.video?.cancelVideoFrameCallback(this.videoFrame);
      this.video?.removeEventListener('playing', this.onVideoPlaying);
      this.video?.removeEventListener('error', this.onVideoError);
      this.cover?.remove();
      this.video = null;
    }
  });
}
